/**
 * RolesSection — the role list + editor entry point (ADR-0030). Built-in roles
 * show a "Built-in" badge and open read-only; custom roles are editable +
 * archivable. The whole surface is permission-gated server-side; `canManage`
 * just hides the affordances.
 *
 * The table is remotely paginated (10 per page) via `roles.listPage` with the
 * standard cursor-stack + `ListPagination` chrome. The member/invite pickers
 * keep using `roles.list` (all roles at once) — a separate procedure.
 */
import { useState } from "react";
import { Alert, Button, ConfirmDialog, toast } from "@flint/ui";
import { Pencil, Plus, Shield, Trash2 } from "lucide-react";
import { ListPagination } from "../../master-list/index.ts";
import { trpc } from "../../../lib/trpc.ts";
import { RoleEditorDialog, type EditableRole } from "./role-editor-dialog.tsx";

const PAGE_SIZE = 10;

export interface RolesSectionProps {
  canManage: boolean;
}

export function RolesSection({ canManage }: RolesSectionProps) {
  const utils = trpc.useUtils();

  // Cursor-stack pagination (the shared list pattern — see the vendors list).
  const [cursorStack, setCursorStack] = useState<string[]>([]);
  const cursor = cursorStack[cursorStack.length - 1];
  const list = trpc.roles.listPage.useQuery(
    { limit: PAGE_SIZE, ...(cursor !== undefined ? { cursor } : {}) },
    { placeholderData: (prev) => prev },
  );
  const departments = trpc.departments.list.useQuery({});

  const [editing, setEditing] = useState<EditableRole | null>(null);
  const [editorOpen, setEditorOpen] = useState(false);
  const [confirmArchive, setConfirmArchive] = useState<EditableRole | null>(null);

  const refreshLists = () =>
    Promise.all([utils.roles.list.invalidate(), utils.roles.listPage.invalidate()]);

  const archive = trpc.roles.archive.useMutation({
    onSuccess: async () => {
      await refreshLists();
      toast.success("Role deleted");
      setConfirmArchive(null);
    },
    onError: (e) => toast.error("Couldn't delete role", { description: e.message }),
  });

  const deptName = (id: string | null) =>
    id ? (departments.data?.departments.find((d) => d.id === id)?.name ?? null) : null;

  const openCreate = () => {
    setEditing(null);
    setEditorOpen(true);
  };
  const openRole = (r: EditableRole) => {
    setEditing(r);
    setEditorOpen(true);
  };

  const rows = list.data?.items ?? [];
  const total = list.data?.total ?? 0;
  const totalPages = Math.max(1, Math.ceil(total / PAGE_SIZE));
  const pageNumber = cursorStack.length + 1;
  const showingFrom = rows.length === 0 ? 0 : cursorStack.length * PAGE_SIZE + 1;
  const showingTo = cursorStack.length * PAGE_SIZE + rows.length;
  const handlePrev = () => setCursorStack((s) => s.slice(0, -1));
  const handleNext = () => {
    const next = list.data?.nextCursor;
    if (next) setCursorStack((s) => [...s, next]);
  };
  /**
   * Jump directly to a page number. Going backwards is just a stack slice
   * (cursors already in hand). Going forwards needs to walk the listPage
   * procedure sequentially because each step's cursor is only known after
   * the previous fetch completes — cursor pagination is one-way per fetch.
   */
  const handleJump = async (target: number) => {
    if (target === pageNumber || target < 1 || target > totalPages) return;
    if (target < pageNumber) {
      setCursorStack((s) => s.slice(0, target - 1));
      return;
    }
    // Walk forward from the current page to the target, building the stack
    // as we go. Uses the vanilla fetcher (`utils.roles.listPage.fetch`) so we
    // get cached pages back without hitting the network when possible.
    const stack = [...cursorStack];
    let cur = stack[stack.length - 1];
    for (let p = pageNumber; p < target; p++) {
      const r = await utils.roles.listPage.fetch({
        limit: PAGE_SIZE,
        ...(cur !== undefined ? { cursor: cur } : {}),
      });
      if (!r.nextCursor) break;
      stack.push(r.nextCursor);
      cur = r.nextCursor;
    }
    setCursorStack(stack);
  };

  return (
    <section className="overflow-hidden rounded-md border border-line bg-paper shadow-sm">
      <header className="flex flex-wrap items-center justify-between gap-3 border-b border-line px-4 py-3">
        <div>
          <h3 className="text-sm font-semibold text-ink">Roles</h3>
          <p className="mt-0.5 text-xs text-muted">
            Reusable bundles of permissions. Built-in roles cover the common garment-factory
            functions; add your own for anything specific.
          </p>
        </div>
        {canManage ? (
          <Button onClick={openCreate}>
            <Plus size={15} />
            New role
          </Button>
        ) : null}
      </header>

      {list.isLoading ? (
        <p className="px-4 py-6 text-sm text-muted">Loading roles…</p>
      ) : list.error ? (
        <div className="p-4">
          <Alert intent="bad" title="Couldn't load roles" description={list.error.message} />
        </div>
      ) : (
        <>
          <table className="w-full text-sm">
            <thead className="bg-raised text-xs uppercase tracking-wide text-muted">
              <tr>
                <th className="px-4 py-2.5 text-left font-medium">Role</th>
                <th className="px-3 py-2.5 text-left font-medium">Department</th>
                <th className="px-3 py-2.5 text-right font-medium">Permissions</th>
                <th className="w-24 px-3 py-2.5" />
              </tr>
            </thead>
            <tbody>
              {rows.map((r) => (
                <tr key={r.id} className="border-t border-line hover:bg-raised/60">
                  <td className="px-4 py-3">
                    <div className="flex items-center gap-2">
                      <button
                        type="button"
                        onClick={() => openRole(r)}
                        className="font-medium text-ink hover:text-sky-700"
                      >
                        {r.name}
                      </button>
                      {r.isSystem ? (
                        <span className="inline-flex items-center gap-1 rounded-full border border-line bg-bg px-1.5 py-0.5 text-xs font-medium text-muted">
                          <Shield size={10} /> Built-in
                        </span>
                      ) : null}
                    </div>
                    {r.description ? (
                      <p className="mt-0.5 max-w-md truncate text-xs text-muted">{r.description}</p>
                    ) : null}
                  </td>
                  <td className="px-3 py-3 text-ink-2">{deptName(r.departmentId) ?? "—"}</td>
                  <td className="px-3 py-3 text-right font-mono text-ink-2">
                    {r.permissions.includes("*") ? "All" : r.permissions.length}
                  </td>
                  <td className="px-3 py-3">
                    <div className="flex items-center justify-end gap-1">
                      <button
                        type="button"
                        onClick={() => openRole(r)}
                        className="rounded p-1.5 text-muted transition hover:bg-bg hover:text-ink-2"
                        aria-label={r.isSystem ? `View ${r.name}` : `Edit ${r.name}`}
                      >
                        <Pencil size={14} />
                      </button>
                      {canManage && !r.isSystem ? (
                        <button
                          type="button"
                          onClick={() => setConfirmArchive(r)}
                          className="rounded p-1.5 text-muted transition hover:bg-bad-50 hover:text-bad"
                          aria-label={`Delete ${r.name}`}
                        >
                          <Trash2 size={14} />
                        </button>
                      ) : null}
                    </div>
                  </td>
                </tr>
              ))}
            </tbody>
          </table>

          <div className="border-t border-line">
            <ListPagination
              pageNumber={pageNumber}
              showingFrom={showingFrom}
              showingTo={showingTo}
              total={total}
              totalPages={totalPages}
              sortLabel="built-in first, then A–Z"
              hasPrev={cursorStack.length > 0}
              hasNext={!!list.data?.nextCursor}
              onPrev={handlePrev}
              onNext={handleNext}
              onJump={handleJump}
            />
          </div>
        </>
      )}

      <RoleEditorDialog
        open={editorOpen}
        onOpenChange={setEditorOpen}
        role={editing}
        departments={departments.data?.departments ?? []}
        onSaved={() => {
          void utils.roles.listPage.invalidate();
          toast.success(editing ? "Role updated" : "Role created");
        }}
      />

      <ConfirmDialog
        open={!!confirmArchive}
        onOpenChange={(open) => {
          if (!open) setConfirmArchive(null);
        }}
        title="Delete role"
        description={
          confirmArchive
            ? `Delete the "${confirmArchive.name}" role? Members assigned to it must be reassigned first.`
            : ""
        }
        intent="danger"
        confirmLabel="Delete role"
        onConfirm={() => {
          if (confirmArchive) archive.mutate({ roleId: confirmArchive.id });
        }}
      />
    </section>
  );
}
